import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Activity, Ban, CalendarClock, Code2, Info, KeyRound, Loader2, Plus, Power, ScrollText, Send, ShieldCheck, Terminal, Trash2, Webhook, type LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { API_TOKEN_SCOPES, WEBHOOK_EVENT_TYPES, type ApiTokenRecord, type ApiTokenScope, type WebhookEndpointRecord } from "@hostdock/types";
import {
  Badge, Button, Checkbox, cn, ConfirmDialog, CopyButton, DataTable, Dialog, EmptyState, ErrorState, Field, formatDateTime, formatRelative, FormError, Input, Notice, OneTimeSecret, Select, Skeleton,
  toast,
} from "@hostdock/ui";
import { api } from "../api";
import { actionsCell, Banner, Cell, PageHero, Pill, primaryButtonClass, rowSecondaryClass, SectionCard, StatCard, tableCell, TableShell } from "../components/page-kit";

const TOKEN_PILL: Record<ApiTokenRecord["status"], { tone: "ok" | "wait" | "off"; label: string }> = { ACTIVE: { tone: "ok", label: "Active" }, EXPIRED: { tone: "wait", label: "Expired" }, REVOKED: { tone: "off", label: "Revoked" } };
const SCOPE_LABEL: Record<ApiTokenScope, string> = {
  "websites:read": "View websites and jobs", "websites:write": "Create and change websites", "databases:read": "View databases", "databases:write": "Create and change databases",
  "domains:read": "View domains", "mail:read": "View email", "mail:write": "Manage email", "migrations:read": "View migrations", "migrations:write": "Manage migrations",
};
const Chip = ({ children, title }: { children: ReactNode; title?: string }) => <span title={title} className="rounded-md bg-surface-3 px-[7px] py-[2px] font-mono text-[11px] text-fg/80">{children}</span>;

export function DevelopersPage() {
  const tokens = useQuery({ queryKey: ["api-tokens"], queryFn: () => api.apiTokens.list() });
  const hooks = useQuery({ queryKey: ["webhooks"], queryFn: () => api.webhooks.list() });
  const t = tokens.data?.items;
  const w = hooks.data?.items;
  const activeTokens = t?.filter((x) => x.status === "ACTIVE");
  const lastUsed = t?.map((x) => x.lastUsedAt).filter((x): x is string => !!x).sort().at(-1);
  const soon = activeTokens?.filter((x) => new Date(x.expiresAt).getTime() - Date.now() < 7 * 86_400_000).length ?? 0;
  const disabled = w?.filter((x) => x.status !== "ACTIVE").length ?? 0;

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero icon={Code2} title="Developers" description="API tokens let scripts and CI use your account without your password. Webhooks tell your own systems when something happens." />
      <Banner tone="info" icon={Info} title="Looking for Git, cron jobs, SSH/SFTP, logs or deployments?">
        Those are managed per website. Open a website from <Link to="/websites" className="font-medium text-accent hover:underline">Websites</Link> and use its tabs.
      </Banner>
      {t && w && (
        <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
          <StatCard icon={KeyRound} tile="teal" label="Active tokens" value={activeTokens!.length} sub={t.length > activeTokens!.length ? `${t.length - activeTokens!.length} expired or revoked` : "Of your API tokens"} />
          <StatCard icon={Activity} tile="blue" label="Last API use" value={lastUsed ? formatRelative(lastUsed) : "Never"} sub="By any of your tokens" />
          <StatCard icon={CalendarClock} tile="orange" label="Expiring soon" value={soon} sub="Active tokens, next 7 days" />
          <StatCard icon={Webhook} tile="green" label="Webhooks" value={w.length} sub={disabled ? `${disabled} disabled` : w.length ? "All receiving events" : "None set up yet"} />
        </div>
      )}
      <ApiTokens q={tokens} />
      <Webhooks q={hooks} />
      <QuickStart />
    </div>
  );
}

// ---------------------------------------------------------------- tokens

function ApiTokens({ q }: { q: UseQueryResult<{ items: ApiTokenRecord[] }> }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiTokenRecord | null>(null);
  const revoke = useMutation({ meta: { inlineError: true }, mutationFn: (t: ApiTokenRecord) => api.apiTokens.revoke(t.id), onSuccess: () => { toast.success("Token revoked"); setRevoking(null); void qc.invalidateQueries({ queryKey: ["api-tokens"] }); } });
  const cols = "xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)_110px_150px_120px_104px] xl:items-center";
  const items = q.data?.items;
  return (
    <SectionCard title="API tokens" count={items?.length} description="Send a token as “Authorization: Bearer <token>”. Each token is limited to the permissions you give it and stops working on its expiry date."
      action={<button type="button" onClick={() => setCreating(true)} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />New token</button>}>
      {q.error ? <div className="py-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        : q.isPending ? <Skeleton className="my-4 h-24 w-full rounded-xl" />
        : !items?.length ? <Empty icon={KeyRound} title="No API tokens" body="Create one for a script or a CI pipeline." />
        : (
          <TableShell flat label="API tokens" cols={cols} headers={["Name", "Access", "Status", "Expires", "Last used", "Actions"]}>
            {items.map((t) => (
              <li key={t.id} role="row" aria-label={t.name} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[66px] xl:gap-0 xl:py-[10px]", t.status !== "ACTIVE" && "opacity-75")}>
                <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                  <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-xl bg-[#d6f5f7] text-[#0a8ea0] dark:bg-surface-3"><KeyRound size={19} strokeWidth={1.7} /></span>
                  <div className="min-w-0 leading-tight">
                    <span className="block truncate text-[13.5px] font-semibold" title={t.name}>{t.name}</span>
                    <span className="mt-[4px] block truncate font-mono text-[11.5px] text-muted">{t.prefix}…</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
                  <Cell label="Access" className={cn(tableCell, "col-span-2 sm:col-span-4 xl:col-span-1 xl:h-auto xl:min-h-[48px]")}>
                    <span className="flex flex-wrap gap-1">{t.scopes.map((sc) => <Chip key={sc} title={SCOPE_LABEL[sc]}>{sc}</Chip>)}</span>
                  </Cell>
                  <Cell label="Status" className={tableCell}><Pill tone={TOKEN_PILL[t.status].tone} dot>{TOKEN_PILL[t.status].label}</Pill></Cell>
                  <Cell label="Expires" className={tableCell}><span title={formatDateTime(t.expiresAt)}>{formatDateTime(t.expiresAt)}</span></Cell>
                  <Cell label="Last used" className={tableCell}><span className={t.lastUsedAt ? undefined : "text-muted"}>{t.lastUsedAt ? formatRelative(t.lastUsedAt) : "Never"}</span></Cell>
                </div>
                <div role="cell" className={actionsCell}>
                  {t.status === "ACTIVE" ? <button type="button" onClick={() => setRevoking(t)} className={cn(rowSecondaryClass, "text-danger xl:w-[88px]")}><Ban size={14} className="shrink-0" />Revoke</button> : <span className="text-[12px] text-subtle">No actions</span>}
                </div>
              </li>
            ))}
          </TableShell>
        )}
      <CreateTokenDialog open={creating} onOpenChange={setCreating} onDone={() => void qc.invalidateQueries({ queryKey: ["api-tokens"] })} />
      <ConfirmDialog
        open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)} title={`Revoke “${revoking?.name}”?`} confirmLabel="Revoke token"
        description="Anything using this token stops working immediately. This cannot be undone."
        loading={revoke.isPending} error={revoke.error} onConfirm={() => revoking && revoke.mutate(revoking)}
      />
    </SectionCard>
  );
}

function Empty({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center px-6 py-9 text-center">
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-soft text-accent"><Icon size={22} /></span>
      <p className="text-[14px] font-semibold">{title}</p>
      <p className="mt-1 max-w-md text-[12.5px] text-muted">{body}</p>
    </div>
  );
}

function CreateTokenDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange(o: boolean): void; onDone(): void }) {
  const [name, setName] = useState("");
  const [days, setDays] = useState("90");
  const [scopes, setScopes] = useState<Set<ApiTokenScope>>(new Set(["websites:read"]));
  const [secret, setSecret] = useState<string | null>(null);
  const create = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.apiTokens.create({ name, scopes: [...scopes], expiresInDays: Number(days) }),
    onSuccess: (t) => { setSecret(t.token); onDone(); },
  });
  const close = (o: boolean) => { if (!o) { setSecret(null); setName(""); setScopes(new Set(["websites:read"])); create.reset(); } onOpenChange(o); };
  const toggle = (s: ApiTokenScope) => setScopes((cur) => { const n = new Set(cur); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  return (
    <Dialog open={open} onOpenChange={close} title={secret ? "Your new API token" : "New API token"}
      footer={secret ? <Button variant="primary" onClick={() => close(false)}>Done</Button> : <><Button onClick={() => close(false)}>Cancel</Button><Button variant="primary" disabled={!name.trim() || scopes.size === 0} loading={create.isPending} onClick={() => create.mutate()}>Create token</Button></>}>
      {secret ? <OneTimeSecret label="Token" value={secret} note="Copy it now. HostDock keeps only a one-way hash and cannot show it again." /> : (
        <div className="space-y-4">
          <Field label="Name" hint="For your own reference, e.g. “GitHub Actions”.">{(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Expires">{(p) => <Select {...p} value={days} onChange={(e) => setDays(e.target.value)}><option value="7">In 7 days</option><option value="30">In 30 days</option><option value="90">In 90 days</option><option value="365">In 1 year</option></Select>}</Field>
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-[13px] font-medium">Allowed actions</legend>
            {API_TOKEN_SCOPES.map((s) => <Checkbox key={s} checked={scopes.has(s)} onChange={() => toggle(s)} label={<span>{SCOPE_LABEL[s]} <span className="font-mono text-[11.5px] text-muted">{s}</span></span>} />)}
            <p className="text-[12px] text-muted">“Change” includes “view”. Tokens can never sign in, change your password or security settings, or manage other tokens.</p>
          </fieldset>
          <FormError error={create.error} />
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- webhooks

function Webhooks({ q }: { q: UseQueryResult<{ items: WebhookEndpointRecord[] }> }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<WebhookEndpointRecord | null>(null);
  const [removing, setRemoving] = useState<WebhookEndpointRecord | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["webhooks"] });
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: (w: WebhookEndpointRecord) => api.webhooks.remove(w.id), onSuccess: () => { toast.success("Webhook deleted"); setRemoving(null); refresh(); } });
  const toggle = useMutation({ mutationFn: (w: WebhookEndpointRecord) => api.webhooks.update(w.id, { enabled: w.status !== "ACTIVE" }), onSuccess: refresh });
  const test = useMutation({ mutationFn: (w: WebhookEndpointRecord) => api.webhooks.test(w.id), onSuccess: () => toast.success("Test event queued. See the delivery log in a few seconds.") });
  const cols = "xl:grid xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_150px_344px] xl:items-center";
  const items = q.data?.items;
  const spin = <Loader2 size={14} className="shrink-0 animate-spin" />;
  return (
    <SectionCard title="Webhooks" count={items?.length} description="HostDock sends a signed HTTPS POST to your URL when an event happens, and retries failures with increasing delays."
      action={<button type="button" onClick={() => setCreating(true)} className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />New webhook</button>}>
      {q.error ? <div className="py-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        : q.isPending ? <Skeleton className="my-4 h-24 w-full rounded-xl" />
        : !items?.length ? <Empty icon={Webhook} title="No webhooks" body="Add a URL to be notified about website, migration and email events." />
        : (
          <TableShell flat label="Webhooks" cols={cols} headers={["Receiver", "Events", "Status", "Actions"]}>
            {items.map((w) => (
              <li key={w.id} role="row" aria-label={w.url} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[66px] xl:gap-0 xl:py-[10px]")}>
                <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                  <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-xl bg-[#e6f0fc] text-[#2f6fd6] dark:bg-surface-3"><Webhook size={19} strokeWidth={1.7} /></span>
                  <div className="min-w-0 leading-tight">
                    <span className="block font-mono text-[12.5px] font-semibold break-all">{w.url}</span>
                    {w.description && <span className="mt-[4px] block truncate text-[11.5px] text-muted" title={w.description}>{w.description}</span>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 xl:contents">
                  <Cell label="Events" className={cn(tableCell, "xl:h-auto xl:min-h-[48px]")}>
                    <span className="flex flex-wrap gap-1">{w.events.includes("*") ? <Chip>All events</Chip> : w.events.map((e) => <Chip key={e}>{e}</Chip>)}</span>
                  </Cell>
                  <Cell label="Status" className={tableCell}>
                    {w.status === "ACTIVE" ? <Pill tone="ok" dot>Active</Pill> : <Pill tone="wait" dot>Disabled</Pill>}
                    {w.status !== "ACTIVE" && w.disabledReason && <span className="max-w-full truncate text-[11px] text-muted" title={w.disabledReason}>{w.disabledReason}</span>}
                  </Cell>
                </div>
                <div role="cell" className={cn(actionsCell, "max-sm:gap-[8px] max-sm:[&>button:not(:last-child)]:px-[8px] max-sm:[&>button:not(:last-child)>svg]:hidden")}>
                  <button type="button" onClick={() => setOpen(w)} className={rowSecondaryClass}><ScrollText size={14} className="shrink-0" />Log</button>
                  <button type="button" disabled={w.status !== "ACTIVE" || (test.isPending && test.variables?.id === w.id)} onClick={() => test.mutate(w)} className={cn(rowSecondaryClass, "disabled:opacity-50")}>
                    {test.isPending && test.variables?.id === w.id ? spin : <Send size={14} className="shrink-0" />}Send test
                  </button>
                  <button type="button" disabled={toggle.isPending && toggle.variables?.id === w.id} onClick={() => toggle.mutate(w)} className={rowSecondaryClass}>
                    {toggle.isPending && toggle.variables?.id === w.id ? spin : <Power size={14} className="shrink-0" />}{w.status === "ACTIVE" ? "Disable" : "Enable"}
                  </button>
                  <button type="button" aria-label={`Delete ${w.url}`} title="Delete" onClick={() => setRemoving(w)} className={cn(rowSecondaryClass, "flex-none px-[10px] text-danger")}><Trash2 size={15} /></button>
                </div>
              </li>
            ))}
          </TableShell>
        )}
      <div className="empty:hidden [&>*]:mb-3"><FormError error={test.error ?? toggle.error} /></div>
      <CreateWebhookDialog open={creating} onOpenChange={setCreating} onDone={(s) => { setSecret(s); refresh(); }} />
      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)} title="Webhook signing secret" footer={<Button variant="primary" onClick={() => setSecret(null)}>Done</Button>}>
        {secret && <>
          <OneTimeSecret label="Signing secret" value={secret} />
          <p className="mt-3 text-[12.5px] text-muted">Each request carries <code>X-HostDock-Signature: t=&lt;unix time&gt;,v1=&lt;hex&gt;</code>, where v1 is HMAC-SHA256 of <code>&lt;t&gt;.&lt;request body&gt;</code> using this secret. Reject requests with an old timestamp.</p>
        </>}
      </Dialog>
      <DeliveryLog webhook={open} onClose={() => setOpen(null)} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Delete this webhook?" confirmLabel="Delete webhook" description={removing?.url}
        loading={remove.isPending} error={remove.error} onConfirm={() => removing && remove.mutate(removing)} />
    </SectionCard>
  );
}

/** How to call the API and check a webhook's signature: static reference, using this Panel's own address. */
function QuickStart() {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const curl = `curl -H "Authorization: Bearer $HOSTDOCK_TOKEN" \\\n  ${origin}/api/v1/websites`;
  const verify = `// Node.js: check X-HostDock-Signature before trusting a delivery
const [t, v1] = header.split(",").map((p) => p.split("=")[1]);
const expected = crypto.createHmac("sha256", secret).update(\`\${t}.\${rawBody}\`).digest("hex");
const fresh = Math.abs(Date.now() / 1000 - Number(t)) < 300;
const ok = fresh && v1?.length === expected.length && crypto.timingSafeEqual(Buffer.from(v1), Buffer.from(expected));`;
  return (
    <div className="mt-[15px] grid gap-[15px] xl:grid-cols-2">
      <CodeCard icon={Terminal} title="Call the API" body="Every token works against the same API the Panel uses. Responses are JSON." code={curl} />
      <CodeCard icon={ShieldCheck} title="Verify a webhook" body="Compute the HMAC over the raw request body and compare it in constant time." code={verify} />
    </div>
  );
}

function CodeCard({ icon: Icon, title, body, code }: { icon: LucideIcon; title: string; body: string; code: string }) {
  return (
    <section className="hd-card flex min-w-0 flex-col px-[18px] py-[15px]" aria-label={title}>
      <div className="flex items-start gap-[12px]">
        <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-[#d6f5f7] text-[#0a8ea0] dark:bg-surface-3"><Icon size={19} strokeWidth={1.8} /></span>
        <div className="min-w-0 flex-1 leading-tight">
          <h2 className="text-[14.5px] font-bold text-fg">{title}</h2>
          <p className="mt-[4px] text-[12.5px] text-muted">{body}</p>
        </div>
        <span className="shrink-0 [&_button]:h-8 [&_button]:px-2.5 [&_button]:text-[12px]"><CopyButton value={code} label="Copy" /></span>
      </div>
      <pre className="hd-scroll mt-[12px] flex-1 overflow-x-auto rounded-xl bg-[#0f1f2c] px-[14px] py-[12px] font-mono text-[11.5px] leading-[18px] text-[#cfe3ec]">{code}</pre>
    </section>
  );
}

function CreateWebhookDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange(o: boolean): void; onDone(secret: string): void }) {
  const [url, setUrl] = useState("https://");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<Set<string>>(new Set(["*"]));
  const create = useMutation({
    mutationFn: () => api.webhooks.create({ url, description, events: [...events] as never }),
    onSuccess: (w) => { onOpenChange(false); setUrl("https://"); setDescription(""); onDone(w.secret); },
  });
  const all = events.has("*");
  const toggle = (e: string) => setEvents((cur) => { const n = new Set(cur); n.delete("*"); if (n.has(e)) n.delete(e); else n.add(e); return n; });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New webhook"
      footer={<><Button onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!url.startsWith("https://") || events.size === 0} loading={create.isPending} onClick={() => create.mutate()}>Create webhook</Button></>}>
      <div className="space-y-4">
        <Field label="Receiver URL" hint="Must be https:// on a public address. Private and internal addresses are refused.">{(p) => <Input {...p} value={url} onChange={(e) => setUrl(e.target.value)} inputMode="url" autoComplete="off" />}</Field>
        <Field label="Description (optional)">{(p) => <Input {...p} value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} />}</Field>
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-[13px] font-medium">Events</legend>
          <Checkbox checked={all} onChange={() => setEvents(all ? new Set() : new Set(["*"]))} label="All events" />
          {WEBHOOK_EVENT_TYPES.filter((e) => e !== "webhook.test").map((e) => <Checkbox key={e} checked={!all && events.has(e)} disabled={all} onChange={() => toggle(e)} label={<span className="font-mono text-[12.5px]">{e}</span>} />)}
        </fieldset>
        <FormError error={create.error} />
      </div>
    </Dialog>
  );
}

const DELIVERY_TONE = { SUCCESS: "success", PENDING: "info", FAILED: "danger" } as const;

function DeliveryLog({ webhook, onClose }: { webhook: WebhookEndpointRecord | null; onClose(): void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["webhook-deliveries", webhook?.id], queryFn: () => api.webhooks.deliveries(webhook!.id), enabled: !!webhook, refetchInterval: 5000 });
  const redeliver = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.webhooks.redeliver(id), onSuccess: () => void qc.invalidateQueries({ queryKey: ["webhook-deliveries"] }) });
  return (
    <Dialog open={!!webhook} onOpenChange={(o) => !o && onClose()} title="Delivery log" description={webhook?.url} size="lg">
      <DataTable
        rows={q.data?.items}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        rowKey={(d) => d.id}
        empty={<EmptyState icon={Webhook} title="Nothing sent yet" />}
        columns={[
          { key: "e", header: "Event", cell: (d) => <span className="font-mono text-[12.5px]">{d.eventType}</span> },
          { key: "s", header: "Result", cell: (d) => <div><Badge tone={DELIVERY_TONE[d.status]} dot>{d.status === "SUCCESS" ? "Delivered" : d.status === "PENDING" ? (d.attempts ? "Retrying" : "Queued") : "Failed"}</Badge>{d.lastError && <p className="mt-0.5 text-[11.5px] text-muted">{d.lastError}</p>}</div> },
          { key: "a", header: "Attempts", cell: (d) => d.attempts },
          { key: "w", header: "When", cell: (d) => <span className="text-muted">{formatRelative(d.createdAt)}</span>, hideOnMobile: true },
          { key: "r", header: "", cell: (d) => (d.status !== "PENDING" ? <Button size="sm" loading={redeliver.isPending && redeliver.variables === d.id} onClick={() => redeliver.mutate(d.id)}>Resend</Button> : null) },
        ]}
      />
      <FormError error={redeliver.error} />
      <Notice tone="info">Receiver responses are not stored. Failed deliveries are retried after 1 minute, 5 minutes, 30 minutes, 2 hours and 6 hours, then stop.</Notice>
    </Dialog>
  );
}
